import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useLocation, useNavigate } from "react-router-dom"
import { AlertCircle, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useAuth } from "@/contexts/AuthContext"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Login
// LEGACY: apps/admin/src/pages/Login.jsx
// API:    POST /admin/login
//
// Request body parity (legacy ↔ v2):
//   email     → email     (string, required, type=email)
//   password  → password  (string, required, type=password)
//
// Success response (200):
//   { success: true,
//     data: { token: string, isAdmin: true },
//     message: "Login Successfully" }
//
// Failed credentials (404, intentional in backend):
//   { success: true, data: [], message: "Login credential are wrong" }
//
// localStorage write parity:
//   key:   auth_data
//   value: JSON.stringify({ token, isAdmin })
//
// Behavior parity:
//   - On success: navigate to /dashboard (or `from` if redirected from a
//     protected route)
//   - On failure: show error, stay on page
//   - On already-signed-in mount: navigate to /dashboard
// ───────────────────────────────────────────────────────────────────────

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Invalid email"),
  password: z.string().min(1, "Password is required"),
})

type FormValues = z.infer<typeof schema>

type LocationState = { from?: string } | null

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, signIn } = useAuth()

  const fromPath =
    (location.state as LocationState)?.from && (location.state as LocationState)?.from !== "/login"
      ? (location.state as LocationState)!.from!
      : "/dashboard"

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  })

  // Already-signed-in users skip the login screen.
  useEffect(() => {
    if (isAuthenticated) navigate(fromPath, { replace: true })
  }, [isAuthenticated, navigate, fromPath])

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null)
    setSubmitting(true)
    try {
      const res = await api.post<{
        success: boolean
        data: { token?: string; isAdmin?: boolean } | unknown[]
        message: string
      }>("/admin/login", values)

      // Backend returns 200 with token, OR 404 with empty data array on bad creds.
      // Axios will only land here on 2xx; the catch handles 4xx.
      const data = res.data?.data
      if (data && !Array.isArray(data) && typeof data.token === "string") {
        signIn({ token: data.token, isAdmin: !!data.isAdmin })
        navigate(fromPath, { replace: true })
      } else {
        setSubmitError("Email/Password is wrong")
      }
    } catch (err) {
      // Mirror legacy message exactly so anyone trained on the old admin
      // recognizes the failure state.
      setSubmitError("Email/Password is wrong")
      void err
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 grid h-12 w-12 place-items-center rounded-md bg-primary text-primary-foreground font-bold">
            GT
          </div>
          <CardTitle>Sign in to Good Talk Admin</CardTitle>
          <CardDescription>
            Use your admin email and password to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@goodtalk.coach"
                aria-invalid={!!errors.email}
                {...register("email")}
              />
              {errors.email && (
                <p className="text-xs text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={!!errors.password}
                {...register("password")}
              />
              {errors.password && (
                <p className="text-xs text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            {submitError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
